/**
 * useDeviceType Hook
 *
 * Reactive hook that classifies the current viewport into
 * desktop / tablet / mobile and provides shorthand booleans.
 *
 * Breakpoints (default, overridable):
 *   - mobile:  width ≤ 768px
 *   - tablet:  769px ≤ width ≤ 1024px
 *   - desktop: width > 1024px
 *
 * Also exposes `isMobile` (mobile only) and `isTouch` (mobile + tablet)
 * for convenience.
 *
 * Injected into PluginContext.ui so plugins can make responsive decisions.
 */

import { useState, useEffect, useMemo } from 'react';
import type { DeviceType } from '../types/page-actions';

export interface DeviceInfo {
  /** Classified device form factor */
  deviceType: DeviceType;
  /** true when viewport ≤ mobile breakpoint */
  isMobile: boolean;
  /** true when viewport ≤ tablet breakpoint (includes mobile) */
  isTouch: boolean;
  /** Current viewport width in pixels */
  viewportWidth: number;
}

interface DeviceBreakpoints {
  /** Max width for mobile (default 768) */
  mobile?: number;
  /** Max width for tablet (default 1024) */
  tablet?: number;
}

function classify(width: number, bp: Required<DeviceBreakpoints>): DeviceType {
  if (width <= bp.mobile) return 'mobile';
  if (width <= bp.tablet) return 'tablet';
  return 'desktop';
}

/**
 * Reactive hook that returns the current device classification.
 *
 * @example
 * ```tsx
 * const { deviceType, isMobile } = useDeviceType();
 * if (isMobile) return <MobileLayout />;
 * ```
 */
export function useDeviceType(breakpoints?: DeviceBreakpoints): DeviceInfo {
  const bp: Required<DeviceBreakpoints> = useMemo(
    () => ({
      mobile: breakpoints?.mobile ?? 768,
      tablet: breakpoints?.tablet ?? 1024,
    }),
    [breakpoints?.mobile, breakpoints?.tablet],
  );

  const [width, setWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    handleResize(); // sync on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return useMemo<DeviceInfo>(() => {
    const deviceType = classify(width, bp);
    return {
      deviceType,
      isMobile: deviceType === 'mobile',
      isTouch: deviceType === 'mobile' || deviceType === 'tablet',
      viewportWidth: width,
    };
  }, [width, bp]);
}

export default useDeviceType;
